import { useDefaultStore } from "@/store";

export class Frame {
	id: number;
	width: number = 100;
	height: number = 100;
	protected minWidth: number = 100;
	protected minHeight: number = 22; // table header height
	top: number;
	left: number;
	dragOffset: { x: number; y: number } = { x: 0, y: 0 };
	resizeStart: { width: number; height: number } = { width: 0, height: 0 };
	handlerDragStart: { x: number; y: number } = { x: 0, y: 0 };
	isActive: boolean = false;
	keepAspectRatio: boolean = false;
	isCollapsed: boolean = false;
	protected previousHeight: number = 100;

	protected constructor(id: number) {
		this.id = id;
	}

	// ********** Delete **********

	public deleteItem() {
		const itemElement: HTMLElement = document.getElementById(`item-${this.id}`);
		itemElement.remove();
	}

	// ********** Create **********

	protected createNewItem(
		top: number,
		left: number,
		width?: number,
		height?: number,
	): HTMLElement {
		this.top = top;
		this.left = left;
		this.width = width || this.width;
		this.height = height || this.height;

		const existingElement = document.getElementById(`item-${this.id}`);
		if (existingElement) return; // Avoid creating the same element again

		const itemElement = this.itemTemplate(
			this.top,
			this.left,
			this.width,
			this.height,
		);

		return itemElement;
	}

	private itemTemplate(
		top: number,
		left: number,
		width?: number,
		height?: number,
	): HTMLElement {
		const itemElement = document.createElement("div");
		itemElement.id = `item-${this.id}`;
		itemElement.className = "item";
		itemElement.style.top = `${top}px`;
		itemElement.style.left = `${left}px`;
		itemElement.style.width = `${width}px`;
		itemElement.style.height = `${height}px`;

		// Add click event listener
		// itemElement.addEventListener("click", () => {});

		itemElement.addEventListener("dblclick", () => {
			if (
				itemElement.classList.contains("eer-table-header-caret-right") ||
				itemElement.classList.contains("eer-table-header-caret-down")
			)
				return;

			useDefaultStore().actionBarBool = true;
		});

		// Add mousedown event listener for drag
		itemElement.addEventListener("mousedown", (event) => {
			if (useDefaultStore().currentMode === useDefaultStore().validModes.DRAG)
				return;
			this.activeItemClear();

			this.activateItem(itemElement);

			const workspaceWrapper = itemElement.parentElement as HTMLElement;
			if (!workspaceWrapper) return;

			// Start drag functionality
			this.startDrag(event, workspaceWrapper);
		});

		return itemElement;
	}

	protected activeItemClear() {
		const itemElement: HTMLElement = document.querySelector(".item-active");
		if (itemElement) {
			const handlers = itemElement.querySelectorAll(".resize-handler");
			handlers.forEach((handler) => handler.remove());

			itemElement.classList.remove("item-active");
		}
		useDefaultStore().objects.forEach((table) => (table.isActive = false));
	}

	private activateItem(itemElement: HTMLElement) {
		itemElement.classList.add("item-active");
		this.isActive = true;
		useDefaultStore().activeItemChangeBool =
			!useDefaultStore().activeItemChangeBool;

		const handlers = [
			"tl", // top-left
			"ml", // middle-left
			"bl", // bottom-left
			"tr", // top-right
			"mr", // middle-right
			"br", // bottom-right
			"tm", // top-middle
			"bm", // bottom-middle
		] as const;

		handlers.forEach((position) => {
			const handler = document.createElement("div");
			handler.className = `resize-handler resize-handler-${position}`;
			this.styleResizeHandler(handler, position);

			// Add mousedown event listener to log handler position
			handler.addEventListener("mousedown", (event) => {
				event.stopPropagation(); // Prevent triggering other click events
				// console.log(`Clicked handler: ${position}`);
				this.startResize(event, position);
			});

			itemElement.appendChild(handler);
		});
	}

	private styleResizeHandler(handler: HTMLElement, position: string) {
		const positions = {
			tl: { top: "-8px", left: "-8px", cursor: "nw-resize" },
			ml: {
				top: "50%",
				left: "-8px",
				transform: "translateY(-50%)",
				cursor: "ew-resize",
			},
			bl: { bottom: "-8px", left: "-8px", cursor: "ne-resize" },
			tr: { top: "-8px", right: "-8px", cursor: "ne-resize" },
			mr: {
				top: "50%",
				right: "-8px",
				transform: "translateY(-50%)",
				cursor: "ew-resize",
			},
			br: { bottom: "-8px", right: "-8px", cursor: "nw-resize" },
			tm: {
				top: "-8px",
				left: "50%",
				transform: "translateX(-50%)",
				cursor: "n-resize",
			},
			bm: {
				bottom: "-8px",
				left: "50%",
				transform: "translateX(-50%)",
				cursor: "n-resize",
			},
		} as const;

		Object.assign(handler.style, positions[position]);
	}

	// ********** Resize **********

	private startResize(event: MouseEvent, position: string) {
		if (this.isCollapsed) return;

		this.resizeStart.width = this.width;
		this.resizeStart.height = this.height;

		const itemElement = document.getElementById(`item-${this.id}`);
		const itemRect = itemElement.getBoundingClientRect();
		const workspaceWrapper = itemElement.parentElement as HTMLElement;
		const workspaceRect = workspaceWrapper.getBoundingClientRect();

		this.handlerDragStart.x = itemRect.x;
		this.handlerDragStart.y = itemRect.y;

		switch (position) {
			case "tl":
				this.handlerDragStart.x = itemRect.x / useDefaultStore().zoomLevel;
				this.handlerDragStart.y = itemRect.y / useDefaultStore().zoomLevel;

				useDefaultStore().isTableResizing = true;
				break;
			case "ml":
				this.handlerDragStart.x = itemRect.x / useDefaultStore().zoomLevel;
				this.handlerDragStart.y =
					itemRect.y / useDefaultStore().zoomLevel - this.height / 2;
				break;
			case "bl":
				this.handlerDragStart.x = itemRect.x / useDefaultStore().zoomLevel;
				this.handlerDragStart.y =
					itemRect.y / useDefaultStore().zoomLevel + this.height;

				useDefaultStore().isTableResizing = true;
				break;
			case "tr":
				this.handlerDragStart.x =
					itemRect.x / useDefaultStore().zoomLevel + this.width;
				this.handlerDragStart.y = itemRect.y / useDefaultStore().zoomLevel;

				useDefaultStore().isTableResizing = true;
				break;
			case "mr":
				this.handlerDragStart.x =
					itemRect.x / useDefaultStore().zoomLevel + this.width;
				this.handlerDragStart.y =
					itemRect.y / useDefaultStore().zoomLevel - this.height / 2;
				break;
			case "br":
				this.handlerDragStart.x =
					itemRect.x / useDefaultStore().zoomLevel + this.width;
				this.handlerDragStart.y =
					itemRect.y / useDefaultStore().zoomLevel + this.height;

				useDefaultStore().isTableResizing = true;
				break;
			case "tm":
				this.handlerDragStart.x =
					itemRect.x / useDefaultStore().zoomLevel + this.width / 2;
				this.handlerDragStart.y = itemRect.y / useDefaultStore().zoomLevel;

				useDefaultStore().isTableResizing = true;
				break;
			case "bm":
				this.handlerDragStart.x =
					itemRect.x / useDefaultStore().zoomLevel + this.width;
				this.handlerDragStart.y =
					itemRect.y / useDefaultStore().zoomLevel + this.height;

				useDefaultStore().isTableResizing = true;
				break;
			default:
				break;
		}

		const onResize = (ev: MouseEvent) =>
			this.onResize(ev, position, workspaceRect, itemElement);
		const stopResize = (ev: MouseEvent) =>
			this.stopResize(workspaceRect, itemElement);

		document.addEventListener("mousemove", onResize);
		document.addEventListener("mouseup", stopResize);

		// Cleanup listeners when resize ends
		document.addEventListener("mouseup", () => {
			document.removeEventListener("mousemove", onResize);
			document.removeEventListener("mouseup", stopResize);
		});
	}

	private onResize(
		event: MouseEvent,
		position: string,
		workspaceRect: DOMRect,
		itemElement: HTMLElement,
	) {
		const dx =
			event.clientX / useDefaultStore().zoomLevel - this.handlerDragStart.x;
		const dy =
			event.clientY / useDefaultStore().zoomLevel - this.handlerDragStart.y;

		const aspectRatio = this.resizeStart.height / this.resizeStart.width;

		switch (position) {
			case "tl":
				this.width = Math.max(this.minWidth, this.resizeStart.width - dx);
				if (this.keepAspectRatio) {
					this.height = this.width * aspectRatio;
				} else {
					this.height = Math.max(this.minHeight, this.resizeStart.height - dy);
				}
				this.top =
					this.handlerDragStart.y + (this.resizeStart.height - this.height);
				this.left =
					this.handlerDragStart.x +
					(this.resizeStart.width - this.width) -
					workspaceRect.left / useDefaultStore().zoomLevel;
				break;
			case "ml":
				this.width = Math.max(this.minWidth, this.resizeStart.width - dx);
				if (this.keepAspectRatio) this.height = this.width * aspectRatio;
				this.left =
					this.handlerDragStart.x +
					(this.resizeStart.width - this.width) -
					workspaceRect.left / useDefaultStore().zoomLevel;
				break;
			case "bl":
				this.width = Math.max(this.minWidth, this.resizeStart.width - dx);
				if (this.keepAspectRatio) {
					this.height = this.width * aspectRatio;
				} else {
					this.height = Math.max(this.minHeight, this.resizeStart.height + dy);
				}
				this.left =
					this.handlerDragStart.x +
					(this.resizeStart.width - this.width) -
					workspaceRect.left / useDefaultStore().zoomLevel;
				break;
			case "tr":
				this.width = Math.max(this.minWidth, this.resizeStart.width + dx);
				if (this.keepAspectRatio) {
					this.height = this.width * aspectRatio;
				} else {
					this.height = Math.max(this.minHeight, this.resizeStart.height - dy);
				}
				this.top =
					this.handlerDragStart.y + (this.resizeStart.height - this.height);
				break;
			case "mr":
				this.width = Math.max(this.minWidth, this.resizeStart.width + dx);
				if (this.keepAspectRatio) this.height = this.width * aspectRatio;
				break;
			case "br":
				this.width = Math.max(this.minWidth, this.resizeStart.width + dx);
				if (this.keepAspectRatio) {
					this.height = this.width * aspectRatio;
				} else {
					this.height = Math.max(this.minHeight, this.resizeStart.height + dy);
				}
				break;
			case "tm":
				this.height = Math.max(this.minHeight, this.resizeStart.height - dy);
				if (this.keepAspectRatio) this.width = this.height / aspectRatio;
				this.top =
					this.handlerDragStart.y + (this.resizeStart.height - this.height);
				break;
			case "bm":
				this.height = Math.max(this.minHeight, this.resizeStart.height + dy);
				if (this.keepAspectRatio) this.width = this.height / aspectRatio;
				break;
			default:
				break;
		}

		if (itemElement) {
			itemElement.style.width = `${this.width}px`;
			itemElement.style.height = `${this.height}px`;
			itemElement.style.top = `${this.top}px`;
			itemElement.style.left = `${this.left}px`;
		}
	}

	private stopResize(workspaceRect: DOMRect, itemElement: HTMLElement) {
		this.workspaceOverflowCheck(workspaceRect, itemElement);

		useDefaultStore().isTableResizing = false;
	}

	// ********** Drag **********

	protected startDrag(event: MouseEvent, workspaceWrapper: HTMLElement) {
		if (useDefaultStore().currentMode !== useDefaultStore().validModes.SELECT)
			return;
		if (!event.target) return;
		const targetElement = event.target as HTMLElement;
		if (
			targetElement.classList.contains("eer-layer") ||
			targetElement.classList.contains("eer-table-header-caret-right") ||
			targetElement.classList.contains("eer-table-header-caret-down")
		)
			return;

		const itemElement = targetElement.closest(".item").getBoundingClientRect();
		this.dragOffset.x =
			(event.clientX - itemElement.left) / useDefaultStore().zoomLevel;
		this.dragOffset.y =
			(event.clientY - itemElement.top) / useDefaultStore().zoomLevel;

		const onDrag = (ev: MouseEvent) => this.onDrag();
		const stopDrag = (ev: MouseEvent) => this.stopDrag(workspaceWrapper);

		document.addEventListener("mousemove", onDrag);
		document.addEventListener("mouseup", stopDrag);

		// Cleanup listeners when drag ends
		document.addEventListener("mouseup", () => {
			document.removeEventListener("mousemove", onDrag);
			document.removeEventListener("mouseup", stopDrag);
		});
	}

	private onDrag() {
		if (useDefaultStore().cursorPosition) {
			this.left = useDefaultStore().cursorPosition.left - this.dragOffset.x;
			this.top = useDefaultStore().cursorPosition.top - this.dragOffset.y;

			const itemElement = document.getElementById(`item-${this.id}`);
			if (itemElement) {
				itemElement.style.top = `${this.top}px`;
				itemElement.style.left = `${this.left}px`;
			}
		}
	}

	private stopDrag(workspaceWrapper?: HTMLElement) {
		if (!workspaceWrapper) return;

		const workspaceRect = workspaceWrapper.getBoundingClientRect();
		const itemElement = document.getElementById(`item-${this.id}`);

		this.workspaceOverflowCheck(workspaceRect, itemElement);
	}

	protected workspaceOverflowCheck(
		workspaceRect: DOMRect,
		itemElement: HTMLElement,
	) {
		if (
			this.top + this.height >
			workspaceRect.height / useDefaultStore().zoomLevel
		) {
			this.top =
				workspaceRect.height / useDefaultStore().zoomLevel - this.height;
			itemElement.style.top = `${this.top}px`;
		}
		if (
			this.left + this.width >
			workspaceRect.width / useDefaultStore().zoomLevel
		) {
			this.left =
				workspaceRect.width / useDefaultStore().zoomLevel - this.width;
			itemElement.style.left = `${this.left}px`;
		}
		if (this.left < 0) {
			this.left = 0;
			itemElement.style.left = `${this.left}px`;
		}
		if (this.top < 0) {
			this.top = 0;
			itemElement.style.top = `${this.top}px`;
		}
	}
}
